/**
 * TemplateSelector - Use Case Template Dropdown
 * ============================================================================
 * DC-1077: Dropdown component for selecting a use case template
 *
 * Features:
 * - Grouped by category (regulatory, research, commercial, operations)
 * - Risk level badges with color coding
 * - Description preview
 * - "Custom Assessment" option for no template
 * - Loading and error states
 *
 * Pattern: Follows ProfileSelector.tsx (DC-324)
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useTemplateList } from './useUseCaseTemplates';
import type {
  UseCaseTemplateSummary,
  TemplateCategory,
  TemplateListFilters,
} from './types';
import {
  CATEGORY_LABELS,
  CATEGORY_ICONS,
  RISK_LEVEL_CONFIG,
} from './types';
import './TemplateSelector.css';

// ============================================================================
// Types
// ============================================================================

export interface TemplateSelectorProps {
  /** Currently selected template ID */
  selectedTemplateId: string | null;
  /** Callback when template selection changes */
  onTemplateSelect: (templateId: string | null, template: UseCaseTemplateSummary | null) => void;
  /** Disable the selector */
  disabled?: boolean;
  /** Optional organization ID */
  organizationId?: string;
  /** Filter options */
  filters?: TemplateListFilters;
  /** Show only system templates */
  showSystemOnly?: boolean;
  /** Compact mode for smaller spaces */
  compact?: boolean;
  /** Optional additional CSS class */
  className?: string;
  /** Placeholder text */
  placeholder?: string;
  /** Allow "no template" selection */
  allowNone?: boolean;
}

// ============================================================================
// Component
// ============================================================================

export const TemplateSelector: React.FC<TemplateSelectorProps> = ({
  selectedTemplateId,
  onTemplateSelect,
  disabled = false,
  organizationId,
  filters,
  showSystemOnly = false,
  compact = false,
  className = '',
  placeholder = 'Select a use case template',
  allowNone = true,
}) => {
  // Fetch templates
  const effectiveFilters: TemplateListFilters = {
    ...filters,
    include_system: showSystemOnly ? true : filters?.include_system,
  };

  const { templates, loading, error, refresh } = useTemplateList(
    organizationId,
    effectiveFilters
  );

  // Group templates by category
  const groupedTemplates = useMemo(() => {
    const groups: Record<TemplateCategory, UseCaseTemplateSummary[]> = {
      regulatory: [],
      research: [],
      commercial: [],
      operations: [],
    };

    templates.forEach((template) => {
      groups[template.category].push(template);
    });

    // Sort each group by name
    Object.keys(groups).forEach((key) => {
      groups[key as TemplateCategory].sort((a, b) => a.name.localeCompare(b.name));
    });

    return groups;
  }, [templates]);

  // Get selected template
  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find((t) => t.id === selectedTemplateId) || null;
  }, [selectedTemplateId, templates]);

  // Handle selection change
  const handleValueChange = (value: string) => {
    if (value === '__none__') {
      onTemplateSelect(null, null);
      return;
    }

    const template = templates.find((t) => t.id === value) || null;
    onTemplateSelect(value, template);
  };

  // Error state
  if (error) {
    return (
      <div className={`ts-container ts-error ${className}`}>
        <span className="ts-error-icon">⚠</span>
        <span className="ts-error-text">{error}</span>
        <button className="ts-retry-btn" onClick={refresh}>
          Retry
        </button>
      </div>
    );
  }

  // Render category group
  const renderCategoryGroup = (category: TemplateCategory) => {
    const categoryTemplates = groupedTemplates[category];
    if (categoryTemplates.length === 0) return null;

    return (
      <SelectGroup key={category} className="ts-category-group">
        <SelectLabel className="ts-category-header">
          <span className="ts-category-icon">{CATEGORY_ICONS[category]}</span>
          <span className="ts-category-label">{CATEGORY_LABELS[category]}</span>
        </SelectLabel>
        {categoryTemplates.map((template) => (
          <SelectItem
            key={template.id}
            value={template.id}
            className="ts-template-item"
          >
            <div className="ts-template-option">
              <div className="ts-template-header">
                <span className="ts-template-name">{template.name}</span>
                <span
                  className="ts-risk-badge"
                  style={{
                    backgroundColor: RISK_LEVEL_CONFIG[template.risk_level].bgColor,
                    color: RISK_LEVEL_CONFIG[template.risk_level].color,
                  }}
                >
                  {RISK_LEVEL_CONFIG[template.risk_level].label}
                </span>
              </div>
              {!compact && template.description && (
                <div className="ts-template-description">
                  {template.description.length > 80
                    ? `${template.description.substring(0, 80)}...`
                    : template.description}
                </div>
              )}
              {!compact && (
                <div className="ts-template-meta">
                  <span className="ts-stations-count">
                    {template.required_stations.length} stations
                  </span>
                  {template.is_system_template && (
                    <span className="ts-system-badge">System</span>
                  )}
                </div>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectGroup>
    );
  };

  return (
    <div className={`ts-container ${compact ? 'ts-compact' : ''} ${className}`}>
      {!compact && <label className="ts-label">Use Case Template</label>}
      <Select
        value={selectedTemplateId || '__none__'}
        onValueChange={handleValueChange}
        disabled={disabled || loading}
      >
        <SelectTrigger className="ts-trigger">
          <SelectValue placeholder={loading ? 'Loading templates...' : placeholder}>
            {selectedTemplate ? (
              <div className="ts-selected-value">
                <span className="ts-selected-icon">
                  {CATEGORY_ICONS[selectedTemplate.category]}
                </span>
                <span className="ts-selected-name">{selectedTemplate.name}</span>
              </div>
            ) : (
              <span className="ts-placeholder">{placeholder}</span>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="ts-content">
          {allowNone && (
            <SelectItem value="__none__" className="ts-none-item">
              <div className="ts-none-option">
                <span className="ts-none-icon">📊</span>
                <span className="ts-none-label">Custom Assessment</span>
                <span className="ts-none-desc">Run all stations with default weights</span>
              </div>
            </SelectItem>
          )}
          {renderCategoryGroup('regulatory')}
          {renderCategoryGroup('research')}
          {renderCategoryGroup('commercial')}
          {renderCategoryGroup('operations')}
        </SelectContent>
      </Select>
      {selectedTemplate && !compact && (
        <div className="ts-selected-summary">
          <span className="ts-summary-label">Selected:</span>
          <span className="ts-summary-name">{selectedTemplate.name}</span>
          <span
            className="ts-summary-risk"
            style={{
              color: RISK_LEVEL_CONFIG[selectedTemplate.risk_level].color,
            }}
          >
            ({RISK_LEVEL_CONFIG[selectedTemplate.risk_level].label})
          </span>
        </div>
      )}
    </div>
  );
};

export default TemplateSelector;
